// src/components/EntryModal.js
import React, { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { apiRequest } from "../services/api";
import { submitWholesaleRequest } from "../services/wholesaleRequests.api";
import {
  FaWhatsapp,
  FaEye,
  FaEyeSlash,
  FaUser,
  FaTimes,
  FaCheck,
} from "react-icons/fa";

function EntryModal({ isOpen, onClose }) {
  const { lang, toggleLanguage } = useContext(LanguageContext);

  const [mode, setMode] = useState("choice"); // choice | login | wholesale | success
  const [uiError, setUiError] = useState("");
  const [uiSuccess, setUiSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [loginForm, setLoginForm] = useState({
    username: "",
    password: "",
  });

  const [wholesaleForm, setWholesaleForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    businessName: "",
    instagramName: "",
    instagramLink: "",
  });

  const TEXT = {
    EN: {
      welcome: "Welcome to ETIKA",
      intro:
        "Login to access member prices, or request a wholesale account to join our team and unlock special offers.",
      loginOptionTitle: "I already have an account",
      loginOptionDesc: "Login and continue as a member.",
      wholesaleOptionTitle: "I want wholesale access",
      wholesaleOptionDesc:
        "Send a request to see prices, get sales, and become part of our team.",
      continueGuest: "Continue as guest",
      loginTitle: "Login to your account",
      username: "Username",
      password: "Password",
      signIn: "SIGN IN",
      signingIn: "SIGNING IN...",
      back: "Back",
      wholesaleTitle: "Wholesale account request",
      wholesaleIntro:
        "Fill this form and we will contact you by WhatsApp or email with your login credentials after approval.",
      fullName: "Full name",
      phone: "Phone number",
      emailOptional: "Email (optional)",
      businessName: "Business name",
      instagramName: "Business Instagram profile name",
      instagramLink: "Instagram page link",
      whatsapp: "CONTACT US ON WHATSAPP",
      submitRequest: "SUBMIT REQUEST",
      submittingRequest: "SUBMITTING...",
      fillLogin: "Please enter username and password.",
      fillWholesale: "Please enter full name, phone number, and business name.",
      phoneInvalid: "Phone number is invalid.",
      duplicatePhone: "This phone number already submitted a wholesale request.",
      successTitle: "Request sent successfully",
      successMessage:
        "Thank you. You can continue as a guest now. After approval, we will send your username and password by WhatsApp or email.",
      done: "Continue shopping",
      language: "AR",
    },
    AR: {
      welcome: "مرحباً بك في ETIKA",
      intro:
        "سجّل الدخول للوصول إلى أسعار الأعضاء، أو أرسل طلب حساب جملة للانضمام إلى فريقنا والحصول على عروض خاصة.",
      loginOptionTitle: "لدي حساب مسبقاً",
      loginOptionDesc: "سجّل الدخول وتابع كعضو.",
      wholesaleOptionTitle: "أريد حساب جملة",
      wholesaleOptionDesc:
        "أرسل طلباً لمشاهدة الأسعار والحصول على العروض والانضمام إلى فريقنا.",
      continueGuest: "المتابعة كضيف",
      loginTitle: "تسجيل الدخول إلى حسابك",
      username: "اسم المستخدم",
      password: "كلمة المرور",
      signIn: "تسجيل الدخول",
      signingIn: "جارٍ تسجيل الدخول...",
      back: "رجوع",
      wholesaleTitle: "طلب حساب جملة",
      wholesaleIntro:
        "املأ هذا النموذج وسنتواصل معك عبر واتساب أو البريد بعد الموافقة لإرسال بيانات الدخول.",
      fullName: "الاسم الكامل",
      phone: "رقم الهاتف",
      emailOptional: "البريد الإلكتروني (اختياري)",
      businessName: "اسم العمل",
      instagramName: "اسم حساب الإنستغرام الخاص بالعمل",
      instagramLink: "رابط صفحة الإنستغرام",
      whatsapp: "تواصل معنا عبر واتساب",
      submitRequest: "إرسال الطلب",
      submittingRequest: "جارٍ الإرسال...",
      fillLogin: "يرجى إدخال اسم المستخدم وكلمة المرور.",
      fillWholesale: "يرجى إدخال الاسم الكامل ورقم الهاتف واسم العمل.",
      phoneInvalid: "رقم الهاتف غير صحيح.",
      duplicatePhone: "رقم الهاتف هذا أرسل طلب حساب جملة من قبل.",
      successTitle: "تم إرسال الطلب بنجاح",
      successMessage:
        "شكراً لك. يمكنك المتابعة كضيف الآن. بعد الموافقة، سنرسل اسم المستخدم وكلمة المرور عبر واتساب أو البريد الإلكتروني.",
      done: "متابعة التسوق",
      language: "EN",
    },
  }[lang];

  const whatsappLink = "https://wa.me/961XXXXXXXX";

 useEffect(() => {
  if (!isOpen) return;

  if (localStorage.getItem("esill-open-login")) {
    setMode("login");
    localStorage.removeItem("esill-open-login");
    return;
  }

  setMode("choice");
}, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setUiError("");
    setUiSuccess("");
  }, [mode, isOpen]);

  if (!isOpen) return null;

  const formatLbLocal = (digits) => {
    const d = digits.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    return [p1, p2, p3].filter(Boolean).join(" ");
  };

  const formatLbPlus961 = (digitsAfter961) => {
    const d = digitsAfter961.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    const parts = [p1, p2, p3].filter(Boolean).join(" ");
    return parts ? `+961 ${parts}` : "+961";
  };

  const formatPhoneInput = (raw) => raw;

const validatePhone = () => true;

  const handleCloseAsGuest = () => {
    sessionStorage.setItem("etika-entry-modal-seen", "1");
    onClose();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setUiError("");
    setUiSuccess("");

    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setUiError(TEXT.fillLogin);
      return;
    }

    try {
      setLoading(true);

      const res = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          username: loginForm.username,
          password: loginForm.password,
        }),
      });

      localStorage.setItem("esill-token", res.token);
      sessionStorage.setItem("etika-entry-modal-seen", "1");

      await apiRequest("/analytics/track", {
        method: "POST",
        body: JSON.stringify({
          action_type: "login_sync",
        }),
      }).catch(() => {});

      window.dispatchEvent(new Event("auth-changed"));
      onClose();
    } catch (err) {
      setUiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWholesaleRequest = async (e) => {
    e.preventDefault();
    if (loading) return;

    setUiError("");
    setUiSuccess("");

    if (
      !wholesaleForm.fullName.trim() ||
      !wholesaleForm.phone.trim() ||
      !wholesaleForm.businessName.trim()
    ) {
      setUiError(TEXT.fillWholesale);
      return;
    }

    if (!validatePhone(wholesaleForm.phone)) {
      setUiError(TEXT.phoneInvalid);
      return;
    }

    try {
      setLoading(true);

      await submitWholesaleRequest(wholesaleForm);

      setUiSuccess(TEXT.successMessage);
      setMode("success");
      sessionStorage.setItem("etika-entry-modal-seen", "1");

      setWholesaleForm({
        fullName: "",
        phone: "",
        email: "",
        businessName: "",
        instagramName: "",
        instagramLink: "",
      });
    } catch (err) {
      if (err.status === 409) {
        setUiError(TEXT.duplicatePhone);
      } else {
        setUiError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `
  w-full h-10
  rounded-md
  border border-gray-300
  bg-white
  px-3
  text-sm
  outline-none
  focus:border-black
  transition
  disabled:bg-gray-100
`;

  const primaryButton = `
  w-full h-10
  rounded-md
  bg-black
  text-white
  text-sm font-semibold tracking-wide
  hover:bg-gray-900
  active:scale-[0.99]
  transition
  disabled:opacity-60
  disabled:cursor-wait
`;

  const secondaryButton = `
  w-full h-10
  rounded-md
  bg-white
  border border-gray-300
  text-gray-900
  text-sm font-semibold
  hover:bg-gray-50
  active:scale-[0.99]
  transition
`;

  return (
  <div
    className="
      fixed inset-0 z-[99999]
      bg-black/45
      backdrop-blur-sm
      flex items-center justify-center
      px-3 py-4
      overflow-x-hidden
    "
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <div
      className="
        relative
        w-full
        max-w-[520px]
        max-h-[92vh]
        overflow-y-auto
        overflow-x-hidden
        bg-white
        rounded-[16px]
        shadow-[0_20px_55px_rgba(0,0,0,0.22)]
        border border-gray-200
        animate-entryPop
      "
    >
      {/* TOP ACTIONS */}
      <button
        type="button"
        onClick={handleCloseAsGuest}
        className={`
          absolute top-3 z-20
          ${lang === "AR" ? "left-3" : "right-3"}
          w-8 h-8 rounded-full
          bg-white
          border border-gray-200
          flex items-center justify-center
          text-gray-800
          hover:bg-gray-50
        `}
      >
        <FaTimes className="text-[13px]" />
      </button>

      <button
        type="button"
        onClick={toggleLanguage}
        className={`
          absolute top-3 z-20
          ${lang === "AR" ? "right-3" : "left-3"}
          h-8 px-3 rounded-full
          bg-white
          border border-gray-200
          text-xs font-semibold
          text-gray-900
          hover:bg-gray-50
        `}
      >
        {TEXT.language}
      </button>

      {/* HEADER */}
      <div className="px-5 pt-8 pb-5 text-center border-b border-gray-100 bg-[#fafafa]">
        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
          <img
            src="/icons/logoEtika.png"
            alt="ETIKA"
            className="w-full h-full object-contain p-2"
          />
        </div>

        <h2 className="text-[21px] md:text-[23px] font-semibold tracking-wide text-gray-900">
          {TEXT.welcome}
        </h2>

        <p className="text-sm text-gray-600 leading-6 max-w-[420px] mx-auto mt-2">
          {TEXT.intro}
        </p>
      </div>

      {/* BODY */}
      <div className="p-5">
        {mode !== "choice" && mode !== "success" && (
          <button
            type="button"
            onClick={() => setMode("choice")}
            className="text-xs text-gray-500 hover:text-black mb-4"
          >
            ← {TEXT.back}
          </button>
        )}

        {(uiError || uiSuccess) && (
          <div
            className={`
              mb-4 rounded-md px-4 py-3 text-sm flex items-start gap-2
              ${
                uiSuccess
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-red-50 border border-red-200 text-red-700"
              }
            `}
          >
            <span className="font-bold">{uiSuccess ? "✓" : "!"}</span>
            <span>{uiSuccess || uiError}</span>
          </div>
        )}

        {/* CHOICE */}
        {mode === "choice" && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setMode("login")}
              className="
                w-full
                text-start
                rounded-md
                border border-gray-300
                p-4
                bg-white
                hover:border-black
                hover:bg-gray-50
                transition
              "
            >
              <h3 className="text-[15px] font-semibold text-gray-900 mb-1">
                {TEXT.loginOptionTitle}
              </h3>

              <p className="text-sm text-gray-600 leading-5">
                {TEXT.loginOptionDesc}
              </p>
            </button>

            <button
              type="button"
              onClick={() => setMode("wholesale")}
              className="
                w-full
                text-start
                rounded-md
                border border-gray-300
                p-4
                bg-white
                hover:border-black
                hover:bg-gray-50
                transition
              "
            >
              <h3 className="text-[15px] font-semibold text-gray-900 mb-1">
                {TEXT.wholesaleOptionTitle}
              </h3>

              <p className="text-sm text-gray-600 leading-5">
                {TEXT.wholesaleOptionDesc}
              </p>
            </button>

            <button
              type="button"
              onClick={handleCloseAsGuest}
              className={secondaryButton}
            >
              {TEXT.continueGuest}
            </button>
          </div>
        )}

        {/* LOGIN */}
        {mode === "login" && (
          <form onSubmit={handleLogin} autoComplete="off" className="space-y-4">
            <h3 className="text-[18px] font-semibold text-gray-900">
              {TEXT.loginTitle}
            </h3>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {TEXT.username}
              </label>
              <div className="relative">
                <input
                  disabled={loading}
                  type="text"
                  name="etika-entry-username"
                  autoComplete="new-password"
                  className={`${inputClass} ${lang === "AR" ? "pr-9" : "pl-9"}`}
                  value={loginForm.username}
                  onChange={(e) =>
                    setLoginForm((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                />
                <FaUser
                  className={`
                    absolute top-1/2 -translate-y-1/2 text-gray-400 text-sm
                    ${lang === "AR" ? "right-3" : "left-3"}
                  `}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {TEXT.password}
              </label>

              <div className="relative">
                <input
                  disabled={loading}
                  type={showLoginPassword ? "text" : "password"}
                  name="etika-entry-passcode"
                  autoComplete="new-password"
                  className={`${inputClass} ${lang === "AR" ? "pl-10" : "pr-10"}`}
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                />

                <button
                  type="button"
                  onClick={() => setShowLoginPassword((v) => !v)}
                  className={`
                    absolute top-1/2 -translate-y-1/2
                    ${lang === "AR" ? "left-3" : "right-3"}
                    text-gray-500 hover:text-black
                  `}
                >
                  {showLoginPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className={primaryButton}>
              {loading ? TEXT.signingIn : TEXT.signIn}
            </button>

            <button
              type="button"
              onClick={handleCloseAsGuest}
              className="w-full h-9 text-sm text-gray-500 hover:text-black"
            >
              {TEXT.continueGuest}
            </button>
          </form>
        )}

        {/* WHOLESALE */}
        {mode === "wholesale" && (
          <form onSubmit={handleWholesaleRequest} className="space-y-4">
            <div>
              <h3 className="text-[18px] font-semibold text-gray-900">
                {TEXT.wholesaleTitle}
              </h3>
              <p className="text-sm text-gray-600 leading-6 mt-1">
                {TEXT.wholesaleIntro}
              </p>
            </div>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="
                w-full h-10 rounded-md
                bg-[#25D366]
                text-white
                text-sm font-semibold
                flex items-center justify-center gap-2
                hover:opacity-90 transition
              "
            >
              <FaWhatsapp />
              {TEXT.whatsapp}
            </a>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.fullName}
                </label>
                <input
                  disabled={loading}
                  className={inputClass}
                  value={wholesaleForm.fullName}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
                      fullName: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.phone}
                </label>
                <input
                  disabled={loading}
                  className={inputClass}
                  value={wholesaleForm.phone}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
phone: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.emailOptional}
                </label>
                <input
                  disabled={loading}
                  type="email"
                  className={inputClass}
                  value={wholesaleForm.email}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.businessName}
                </label>
                <input
                  disabled={loading}
                  className={inputClass}
                  value={wholesaleForm.businessName}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
                      businessName: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.instagramName}
                </label>
                <input
                  disabled={loading}
                  className={inputClass}
                  value={wholesaleForm.instagramName}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
                      instagramName: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {TEXT.instagramLink}
                </label>
                <input
                  disabled={loading}
                  className={inputClass}
                  value={wholesaleForm.instagramLink}
                  onChange={(e) =>
                    setWholesaleForm((prev) => ({
                      ...prev,
                      instagramLink: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className={primaryButton}>
              {loading ? TEXT.submittingRequest : TEXT.submitRequest}
            </button>
          </form>
        )}

        {/* SUCCESS */}
        {mode === "success" && (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 text-green-700 flex items-center justify-center mx-auto mb-4">
              <FaCheck className="text-lg" />
            </div>

            <h3 className="text-[20px] font-semibold text-gray-900 mb-2">
              {TEXT.successTitle}
            </h3>

            <p className="text-sm text-gray-600 leading-6 max-w-md mx-auto mb-5">
              {TEXT.successMessage}
            </p>

            <button
              type="button"
              onClick={handleCloseAsGuest}
              className={primaryButton}
            >
              {TEXT.done}
            </button>
          </div>
        )}
      </div>
    </div>

    <style>{`
      @keyframes entryPop {
        from {
          transform: translateY(18px) scale(0.98);
          opacity: 0;
        }
        to {
          transform: translateY(0) scale(1);
          opacity: 1;
        }
      }

      .animate-entryPop {
        animation: entryPop 0.22s ease-out;
      }
    `}</style>
  </div>
);
}

export default EntryModal;
